/**
 * What the line-item section says, where a runner can reach it — the reason
 * `campaign-copy.ts` is a module rather than strings in a component.
 *
 * 🔴 The sentences that matter most here are the ones about what a line item IS,
 * not about what this console cannot do — and the difference stopped being academic
 * on 2026-10-08, when the owner let this console switch a line item on. Until then
 * these strings leaned on "there is no update in v1", which was true and is now
 * false; a buyer is owed the facts, which have not moved:
 *
 * - an untargeted line item is not harmless — X's documentation says no targeting
 *   means all users worldwide, and the console that can now throw the switch is the
 *   reason to say so louder rather than quieter;
 * - its price under `AUTO` is X's, not ours;
 * - X reports no per-line-item delivery signal, so the green or red an operator
 *   looks for belongs to the campaign above it — and `ACTIVE` here is not it.
 *
 * Nothing here turns a budget or a bid into money. A bid is the number X holds, with
 * its unit named — `campaign-copy.ts`'s rule, and `line-item-copy.test.ts` holds it
 * for these strings too.
 */
import type { LineItem, LineItemObjective } from "./api.ts";
import { microMoney } from "./money.ts";

/**
 * 🔴 The two objectives v1 offers, in the contract's order, each with why it is here.
 * The list is the contract's (docs/API-CONTRACT.md, POST /api/v1/line-items, 2026-10-20)
 * and nothing wider: a third value here would be a request the worker refuses as «not
 * offered (not measured)». `line-item-copy.test.ts` holds the two against the contract.
 */
export const OBJECTIVES: ReadonlyArray<{ value: LineItemObjective; label: string }> = [
	{ value: "ENGAGEMENTS", label: "ENGAGEMENTS — engagements with the tweet; X's own example" },
	{
		value: "WEBSITE_CLICKS",
		label: "WEBSITE_CLICKS — clicks to a website; the tweet needs a website link or card",
	},
];

/** Under the objective select: what X says the objective decides, and what it does not. */
export const OBJECTIVE_NOTE =
	"The objective is what X optimises and bills for (CPE for ENGAGEMENTS, CPLC for WEBSITE_CLICKS). All line items under one campaign must share it. The product stays PROMOTED_TWEETS in v1.";
import type { LineItemOutcome } from "./line-items.ts";

/** `created: true` — the contract's own words, with the line item's noun. */
export const EXISTS = {
	title: "The line item exists — do not retry, look it up.",
	body: "created: true means the object exists, whatever else the response says; do not retry — a retry creates a second line item. Look the id up.",
};

/** `created: "unknown"` — and here it is worse than for a campaign. */
export const UNKNOWN = {
	title: "The line item may exist — do not retry yet.",
	body: "The create may have gone through, and the answer carried no id to check with. Look at this campaign's line items in X before trying again.",
};

/**
 * Under Create, while it is locked after `exists` or `unknown`.
 *
 * 🔴 It does not say "reload the list and look for it" — the mistake twads#87 fixed
 * for campaigns. X's list lags behind a create by minutes (measured 2026-09-29 on
 * campaigns; nothing X publishes says line items differ), so in exactly those
 * minutes the row is missing from it, and an instruction to look there is one the
 * screen makes impossible to follow.
 */
export const CREATE_LOCKED =
	"Create is locked: a line item for this campaign may already exist. X's list can take minutes to show a new one, so not seeing it below does not mean it was not created.";

/** The deliberate way out, for the answer that carries no id. */
export const CREATE_ANYWAY = {
	label: "Create another anyway",
	title: "This may create a second line item.",
	body:
		"Nothing here can tell you whether the first one exists: the answer carried no id, and X's " +
		"list does not show a new line item for the first few minutes. Check this campaign in X " +
		"before you do this. A second line item can be deleted from the campaign card once it is " +
		"read there, and only while PAUSED — and a line item is the object that spends.",
	confirm: "I checked in X — create another",
};

/** Said beside the lock, because a lock someone cannot pass is a lock they route around. */
export const CREATE_LOCK_IS_LOCAL =
	"Reloading this page clears the warning — it is kept in the screen, not in the account.";

/**
 * 🔴 What this console can and cannot say about a new line item delivering — and the
 * sentence that was WRONG here until 2026-10-07, in the direction that reassures.
 *
 * It used to say X needs a targeting criterion before it will serve a line item, so
 * one made here could not deliver. Two readings of X's own documentation say
 * otherwise, and both were checked:
 *
 * - `docs.x.com/x-ads-api/campaign-management`, the Targeting section (read
 *   2026-10-07): *"If no targeting criteria is specified for a line item, the line
 *   item will target all users worldwide."* So no targeting is not "no delivery" —
 *   it is **everyone**.
 * - *"It is required that you specify at least one primary targeting criterion"*
 *   sits on `…/reference` under **Audience Estimate**, not under `POST line_items`
 *   and not under `targeting_criteria`. Counted: the phrase occurs once on that
 *   page, there. twads#90 §1 attributed it to the line-item chain, and this file
 *   carried that attribution into a warning.
 *
 * 🔴 **A third claim in this sentence had expired before either correction, and d6
 * found it on main, 2026-10-08:** *"or ask for the targeting work to be built"*. The
 * targeting work was built — `POST /api/v1/targeting-criteria` narrows a line item to
 * a place, and the form that calls it opens from the **same row** this banner is shown
 * on. So the banner was sending an operator to X to ask for something one click away.
 * It carried no expired *word*, only an expired *claim*, which is why no scan found it
 * and reading did. The sentence now says what this console can narrow (a place, and
 * since 2026-10-23 a language, a gender and an age bucket, since 2026-10-29 an operating
 * system and a device model) and what it cannot (the other kinds, which X conditions on
 * each other — `KINDS_ADDED` in `targeting-copy.ts`
 * has the conditions).
 *
 * 🔴 **The second correction, 2026-10-08, and it goes the dangerous way.** This
 * paragraph used to end: *"nothing made here can deliver, because it is created
 * PAUSED and v1 has no update of any kind — there is no switch in this console to
 * throw"*, and the danger it named was somebody else's, in X. The owner lifted that
 * on 2026-10-08: `PUT /api/v1/line-items/:id` now accepts `ACTIVE`, so **the switch
 * is in this console**, two clicks from a line item with no targeting criteria. The
 * reading of X's documentation has not changed; what changed is who can act on it,
 * and the warning below is kept for that reason rather than softened because the
 * create is no longer the end of the story.
 *
 * Neither of these is a measurement of a live line item. Nobody here has seen one.
 *
 * 🔴 PR 3 of the one-screen layout (2026-10-29) moved the places this body names: it said
 * «the Start button sits under the Targeting button on this row» and «Narrow it first, with
 * the same Targeting button». Start and the narrowing are on the line item's own screen,
 * opened by Open on its row.
 */
export const NO_TARGETING = {
	title: "This line item has no targeting, and no targeting means everyone.",
	body: "It was created PAUSED, and since 2026-10-08 this console can switch it on — the Start button is on the line item's own screen, opened by Open on its row. X's documentation says a line item with no targeting criteria targets all users worldwide, so starting it before anything narrows it would reach everyone. Narrow it first, on the same screen — a place, a language, a gender, an age bucket, an operating system or a device model; the other kinds of criterion are added in X.",
};

/**
 * 🔴 The create form's heading. Until 2026-10-20 it was a literal on the screen —
 * «created PAUSED, and it cannot deliver without targeting» — the reassuring
 * reading NO_TARGETING above was rewritten away from on 2026-10-07, surviving two
 * rows below the banner that contradicts it (spa-review #3). Here, where a runner
 * reads it, and saying the same thing as the banner: no targeting is a reach.
 */
export const CREATE_HEADING =
	"New line item — created PAUSED; with no targeting it would reach everyone once started";

/**
 * 🔴 Beside the row's Open button, and on the card's row, when attaching would be refused. The refusal
 * itself lives in the Ads panel (this console attaches only to a PAUSED line
 * item, because X creates the promoted tweet ACTIVE); until 2026-10-20 the row said
 * nothing, and the owner opened the panel, found Promote disabled, and stopped.
 * `null` for PAUSED: nothing to say, nothing rendered. Until PR 3 (2026-10-29) it ended
 * «Stop is under Targeting on this row»; the switch is Pause, on the line item's screen —
 * named by the panel and not by «this row», because the card's row has no Open.
 */
export function creativeHint(entityStatus: string): string | null {
	if (entityStatus === "PAUSED") return null;
	return "Pause first to attach: this console attaches a creative only to a PAUSED line item, because X creates the promoted tweet ACTIVE. Pause is on the line item's own screen in the Line items panel.";
}

/** Beside the create form: the flight is the operator's figure, and why. */
export const FLIGHT_IS_YOURS =
	"Start and end are both required. X's reference marks the end required and X's own example leaves it out, so this console asks you rather than choosing: a line item that stops on a date and one that serves until stopped differ by every day in between.";

/**
 * Beside an AUTO bid: the price is X's. Since PR 5 (2026-10-23) AUTO is the default, not the
 * only choice — the Create ad wizard offers a maximum bid and a target cost — so the sentence
 * is about AUTO, on both screens, and no longer says it is all there is.
 */
export const PRICE_IS_X_TITLE = "Under AUTO, X sets the price.";
export const PRICE_IS_X =
	"Under AUTO, the one bid that needs no price from this console, X optimises against the campaign's daily budget and flight. Nobody here can tell you in advance what an engagement will cost.";

/** Under the list: why there is no green or red per line item. */
export const NO_SERVABLE =
	"X reports no per-line-item delivery signal — no servable, no reason. Whether anything can serve is the campaign's answer, above.";

/**
 * What the line-item form under a campaign sends, shown so an operator does not look for the
 * controls. Since PR1 of stage 1 (2026-10-21) the objective is NOT on this list — it is the
 * select rendered above this sentence. 🔴 Since PR 5 (2026-10-23) placements and the bid are
 * choices in the Create ad wizard, so this sentence says what THIS form sends and where the
 * choice is, rather than «fixed in v1», which stopped being true; the wizard renders
 * `PRODUCT_FIXED` (line-item-fields-copy.ts) instead.
 */
export const FIXED_SHAPE =
	"This form sends placement ALL_ON_TWITTER, product PROMOTED_TWEETS and bid AUTO, as in X's own example; the objective is the select above. Placements and the bid are chosen in Create ad; other products need fields v1 does not carry.";

/**
 * The read-only line, and it says the gate it actually is: write access to the ad
 * account, the same grant the campaign create needs. The separate
 * `twads:line-items:write` scope is a machine caller's gate and is not what stops a
 * person here — saying otherwise would send an operator asking for the wrong thing.
 */
export const READ_ONLY =
	"You can read this campaign's line items. Creating one needs write access to this ad account — the same access the campaign create needs.";

/**
 * Under the list: what is on screen, and nothing about what is not.
 *
 * No "N of M" — X's `with_total_count` is exclusive with `cursor`, so a screen that
 * pages has no total and does not invent one.
 */
export function loadedLabel(shown: number, more: boolean): string {
	const rows = `${shown} ${shown === 1 ? "line item" : "line items"} loaded`;
	return more ? `${rows}. X has more.` : `${rows}.`;
}

/** X has a page beyond this one. The campaign screen's banner, for this list, without «page» as there. */
export const MORE_BANNER = {
	title: "This list is not complete.",
	body: "X has more line items under this campaign than the ones read so far. Load more reads the next ones from X. How many there are in total is something X does not say while paging, so this screen does not say it either.",
};

/** A bid as X holds it: the number, in micro units — or X's own, under AUTO. */
export function bidText(item: Pick<LineItem, "bidStrategy" | "bidAmountLocalMicro">): string {
	if (item.bidAmountLocalMicro === null) {
		return item.bidStrategy === "AUTO"
			? "AUTO — X sets the price"
			: `${item.bidStrategy} — no amount`;
	}
	return `${item.bidStrategy} — ${String(item.bidAmountLocalMicro)} micro`;
}

/**
 * The line item's budgets as X holds them, in the campaign's currency. `null` — X reported
 * null — is «none set», never 0; a field X's answer does not carry is «not in X's answer»,
 * because that is all the console knows about it.
 */
export function lineItemBudgetText(
	item: Pick<LineItem, "totalBudgetAmountLocalMicro" | "dailyBudgetAmountLocalMicro">,
	currency: string | null,
): string {
	const one = (micro: number | null | undefined) =>
		micro === undefined
			? "not in X's answer"
			: micro === null
				? "none set"
				: microMoney(micro, currency);
	return `Total ${one(item.totalBudgetAmountLocalMicro)} · daily ${one(item.dailyBudgetAmountLocalMicro)}`;
}

/** A flight as X holds it, with an absent end named rather than left blank. */
export function flightText(item: Pick<LineItem, "startTime" | "endTime">): string {
	const start = item.startTime ?? "no start";
	return `${start} → ${item.endTime ?? "no end — it serves until stopped"}`;
}

/**
 * The banner title after a create. 🔴 Until 2026-10-20 it said «This console has
 * no switch for it» — true when written and false since 2026-10-08, when the Start
 * and Stop buttons arrived under the row's Targeting button (the header above says
 * these strings were revised for that day; this one was missed, spa-review #2). The
 * sentence now says where the switches are, and keeps the fact that matters more:
 * no targeting means everyone once started. PR 3 (2026-10-29) moved them again, from
 * «under this row's Targeting button» to the line item's own screen.
 */
export function createdText(id: string): string {
	return `Created PAUSED — ${id}. It carries no targeting, and no targeting means everyone once it is started; Start and Pause are on its own screen, opened by Open on its row, so narrow it there first.`;
}

/** The title of the outcome banner, so the component decides nothing. */
export function outcomeTitle(outcome: LineItemOutcome): string {
	switch (outcome.kind) {
		case "created":
			return createdText(outcome.id);
		case "exists":
			return EXISTS.title;
		case "unknown":
			return UNKNOWN.title;
		default:
			return "Nothing was created.";
	}
}
